/* eslint-disable @typescript-eslint/ban-ts-comment */
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import {
  getWeek,
  getYear,
  isPickAndMixProduct,
  nextDeliveryWeekDateObj,
} from "@chef/helpers";
import {
  useExtendedBasketData,
  useFavorites,
  usePickAndMixPrice,
} from "@chef/state-management/hooks";
import {
  loadCalendar,
  selectDeviationProducts,
  selectHasSetInitialPrice,
  setDeviation,
  setHasSetInitialPrice,
  AppDispatch,
  PickAndMixQuery,
} from "@chef/state-management";
import { isEqualStrings } from "@chef/utils/equal";
import {
  getVariationFromPickAndMix,
  isVariationPlusPortions,
} from "@chef/state-management/helpers";
import { nonNullable } from "@chef/utils/nonNullable";
import { useEffectOnce } from "@chef/hooks";
import { isNotEmptyArray } from "@chef/utils/array";

export const useCreateDeviationBasket = (args: {
  week: number;
  year: number;
}) => {
  const { week, year } = args;

  const currentDate = nextDeliveryWeekDateObj();
  const currentWeek = getWeek(currentDate);
  const currentYear = getYear(currentDate);

  const dispatch = useDispatch<AppDispatch>();

  const { getPriceOfPickAndMixProduct } = usePickAndMixPrice({
    week,
    year,
  });

  // load calendar
  useEffect(() => {
    dispatch(loadCalendar({ week: currentWeek, year: currentYear, range: 5 }));
  }, [currentWeek, currentYear, dispatch]);

  // selectors
  const selectedProducts = useSelector(selectDeviationProducts({ week, year }));

  const hasSetInitialPrice = useSelector(
    selectHasSetInitialPrice({ week, year }),
  );

  useEffect(() => {
    // set initial price data (it won't be correct when we fetch it from calendar)
    if (hasSetInitialPrice) {
      return;
    }
    if (!selectedProducts) {
      return;
    }

    dispatch(setHasSetInitialPrice({ week, year, set: true }));

    const newDeviation = selectedProducts.map((sp) => {
      if (!isPickAndMixProduct(sp)) {
        return sp;
      }

      return {
        ...sp,
        price:
          getPriceOfPickAndMixProduct({
            productId: sp.productId,
            variationId: sp.variationId,
          }) || 0,
      };
    });

    dispatch(
      setDeviation({
        basket: {
          products: newDeviation,
          week,
          year,
        },
      }),
    );
  }, [selectedProducts, week, year]);

  const isLoading = selectedProducts === undefined;

  return {
    isLoading,
  };
};

interface UsePickAndMixProductWithPrice {
  sortedPickAndMix: PickAndMixQuery["pickAndMix"];
  week: number;
  year: number;
  signup?: boolean;
}

export const usePickAndMixProductWithPrice = ({
  sortedPickAndMix,
  week,
  year,
}: UsePickAndMixProductWithPrice) => {
  const { getPriceOfPickAndMixProduct } = usePickAndMixPrice({
    week,
    year,
  });

  const selectedProducts = useSelector(selectDeviationProducts({ week, year }));

  const {
    data: extendedBasketData,
    isLoading: isLoadingExtendedBasketData,
    isError: isErrorExtendedBasketData,
  } = useExtendedBasketData({ week, year });

  const pickAndMixProductWithPrice = sortedPickAndMix
    .flatMap((pnm) =>
      pnm.recipes.flatMap(() => {
        const variationIds = sortedPickAndMix.flatMap((pickAndMix) =>
          pickAndMix.recipes.flatMap((recipe) => {
            return recipe.portions
              .filter((p) => p.size === extendedBasketData.portions)
              .map((p) => p.variationId);
          }),
        );

        const { product } = pnm;

        const variations = variationIds
          .map((variationId) =>
            getVariationFromPickAndMix({
              pickAndMix: sortedPickAndMix,
              productId: product.productId,
              variationId: variationId,
            }),
          )
          .filter((variation) => variation !== null);

        const plusProteinVariation = variations.find((variation) =>
          isVariationPlusPortions(variation || { attributes: [] }),
        );

        const variation = variations.find(
          (v) => !isVariationPlusPortions(v || { attributes: [] }),
        );

        // if the variation don't exist, it means it was filtered out by filterPickAndMix
        if (!variation) {
          return null;
        }

        const plusVariationIsInBasket = selectedProducts?.some((p) =>
          isEqualStrings(p.variationId, plusProteinVariation?.variationId),
        );

        const variationIsInBasket = selectedProducts?.some((p) =>
          isEqualStrings(p.variationId, variation?.variationId),
        );

        const hasPlusProtein = !!plusProteinVariation;

        const productIsInBasket =
          variationIsInBasket || plusVariationIsInBasket;

        const variationId = plusVariationIsInBasket
          ? plusProteinVariation?.variationId || ""
          : variation.variationId;

        const price = getPriceOfPickAndMixProduct({
          variationId,
          product,
        });

        const recipes = pnm.recipes.map((recipe) => {
          return {
            ...recipe,
            price: price ?? 0,
            variation,
            productIsInBasket,
            hasPlusProtein,
            plusVariationIsInBasket,
          };
        });

        return {
          ...pnm,
          recipes,
        };
      }),
    )
    .filter(nonNullable);

  const negativePriceRecipes = pickAndMixProductWithPrice
    .flatMap((p) => p.recipes)
    .filter((r) => r.price < 0);

  return {
    pickAndMix: pickAndMixProductWithPrice,
    negativePriceRecipes,
    isLoadingExtendedBasketData,
    isErrorExtendedBasketData,
  };
};

type PickAndMixWithPrice = ReturnType<
  typeof usePickAndMixProductWithPrice
>["pickAndMix"];

interface UseSortedPickAndMix {
  pickAndMix: PickAndMixWithPrice;
  week: number;
  year: number;
}

export const useSortedPickAndMix = ({
  pickAndMix,
  week,
  year,
}: UseSortedPickAndMix) => {
  const [sortedPickAndMix, setSortedPickAndMix] = useState<PickAndMixWithPrice>(
    [],
  );

  const { isDisliked, isFavorited, isReady: isFavoritesReady } = useFavorites();

  const {
    data: { selectedProducts, portions },
    isLoading: isLoadingExtendedBasketData,
  } = useExtendedBasketData({ week, year });

  // We want pickAndMix to be sorted in the following order. After a finished sort, we want to lock the order,
  // so that the user can't change it by clicking on the pickAndMix products.
  // 1. Selected products
  // 2. Dishes with 0 cost
  // 3. Dishes with negative cost, sorted by price in ascending order
  // 4. Dishes with positive cost, sorted by price in ascending order
  // 5. Disliked recipes
  const compareFn = (
    a: PickAndMixWithPrice[number],
    b: PickAndMixWithPrice[number],
  ) => {
    const aRecipe = a.recipes[0];
    const bRecipe = b.recipes[0];

    const aIsDisliked = isDisliked(aRecipe);
    const bIsDisliked = isDisliked(bRecipe);

    const aIsFavorited = isFavorited(aRecipe);
    const bIsFavorited = isFavorited(bRecipe);

    const aIsPicked = selectedProducts?.some((p) =>
      isEqualStrings(p.productId, a.productId),
    );

    const bIsPicked = selectedProducts?.some((p) =>
      isEqualStrings(p.productId, b.productId),
    );

    if (aIsPicked && !bIsPicked) {
      return -1;
    }

    if (!aIsPicked && bIsPicked) {
      return 1;
    }

    if (aIsDisliked && !bIsDisliked) {
      return 1;
    }

    if (!aIsDisliked && bIsDisliked) {
      return -1;
    }

    if (aRecipe.price === 0 && bRecipe.price !== 0) {
      return -1;
    }

    if (aRecipe.price !== 0 && bRecipe.price === 0) {
      return 1;
    }

    if (aRecipe.price < 0 && bRecipe.price > 0) {
      return -1;
    }

    if (aRecipe.price > 0 && bRecipe.price < 0) {
      return 1;
    }

    if (aRecipe.price === bRecipe.price && aIsFavorited && !bIsFavorited) {
      return -1;
    }

    if (aRecipe.price === bRecipe.price && !aIsFavorited && bIsFavorited) {
      return 1;
    }

    return aRecipe.price - bRecipe.price;
  };

  useEffectOnce(
    () => {
      setSortedPickAndMix([...pickAndMix].sort(compareFn));
    },
    !isLoadingExtendedBasketData &&
      isFavoritesReady &&
      isNotEmptyArray(pickAndMix),
    portions,
  );

  return { sortedPickAndMix };
};

interface UseScrollIntoViewHighlightOptions {
  dataAttribute?: string;
  scrollBehavior?: ScrollBehavior;
  highlight?: boolean;
  highlightClassNames?: string[];
  duration?: number;
  shouldScroll?: boolean;
}
/**
 * useScrollIntoViewHighlight
 *
 * A React hook that scrolls an element into view and applies a temporary highlight effect,
 * typically used when navigating via query parameters like `?recipeId=123`.
 *
 * The element is selected via a `data-*` attribute matching the provided `id`.
 * This is useful for jumping to a specific card or section on initial page load.
 *
 * @param id - The ID of the element to scroll to (e.g. from a URL query param). If `undefined`, the hook does nothing.
 * @param options - Configuration options:
 *   - dataAttribute (string): The `data-*` attribute used to identify the element. Defaults to `"data-recipe-id"`.
 *   - scrollBehavior: Controls the scroll animation. Defaults to `"smooth"`.
 *   - highlight (boolean): Whether to apply a temporary highlight ring to the element. Defaults to `true`.
 *   - highlightClassNames (string[]): The CSS classes applied during highlight. Defaults to `["ring", "ring-highlight", "transition"]`.
 *   - duration (number): Duration (in ms) the highlight stays before being removed. Defaults to `2000`.
 *   - shouldScroll (boolean): Controls whether the scroll and highlight logic should run. This is useful for deferring execution
 *     until the DOM is ready — for example, after recipe cards have been rendered. If `false`, the hook does nothing. Defaults to false

 */
export const useScrollIntoViewHighlight = (
  id: string | undefined,
  {
    dataAttribute = "data-recipe-id",
    scrollBehavior = "smooth",
    highlight = true,
    highlightClassNames = ["ring", "ring-highlight", "transition"],
    duration = 2000,
    shouldScroll = false,
  }: UseScrollIntoViewHighlightOptions = {},
) => {
  useEffect(() => {
    if (!id || !shouldScroll) {
      return;
    }

    const selector = `[${dataAttribute}="${id}"]`;
    const element = document.querySelector(selector) as HTMLElement | null;

    if (!element) {
      return;
    }

    element.scrollIntoView({ behavior: scrollBehavior, block: "center" });

    if (highlight) {
      element.classList.add(...highlightClassNames);
      setTimeout(() => {
        element.classList.remove(...highlightClassNames);
      }, duration);
    }
  }, [id, dataAttribute, shouldScroll]);
};
